//Node Modules
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRecoilValueLoadable, useSetRecoilState } from "recoil";
import _ from "lodash";

//GraphQL

//Pages

//BinaryForge Components
import { PageHeader } from "../../../components/layout";
import { AlarmCard } from "../../../components/alarm";
import { Note } from "../../../components/helpers";

//3rd Party Components
import { ProgressSpinner } from "primereact/progressspinner";

//Atoms
import { deviceSensorAlarmListSelector } from "../../../atoms/alarm";

//Helpers

//Other
import { nav } from "../../../config/navigation";

//Group active alarms by alarm group - a device can belong to several groups, so its alarm
//appears under each; alarms for devices in no group fall into an ungrouped bucket
const buildGroupedAlarms = (alarms) => {
	const groupsById = new Map();
	const ungrouped = [];

	alarms.forEach((alarmItem) => {
		//Membership on either side counts - an output FS2 with its own sensor alarm belongs with its group
		const memberships = [
			...(alarmItem.device?.alarmGroupsInput?.items ?? []),
			...(alarmItem.device?.alarmGroupsOutput?.items ?? []),
		];
		const groups = _.uniqBy(
			memberships.map((item) => item?.alarmGroup).filter((group) => group?.id),
			"id",
		);

		if (groups.length === 0) {
			ungrouped.push(alarmItem);
			return;
		}

		groups.forEach((group) => {
			if (!groupsById.has(group.id)) {
				groupsById.set(group.id, { id: group.id, name: group.name ?? group.id, alarms: [] });
			}
			groupsById.get(group.id).alarms.push(alarmItem);
		});
	});

	return { grouped: _.orderBy([...groupsById.values()], ["name"], ["asc"]), ungrouped };
};

const Alarms = () => {
	const { t } = useTranslation();

	//Recoil State
	const { state, contents: deviceSensorAlarmList } = useRecoilValueLoadable(deviceSensorAlarmListSelector);
	// const setDeviceSensorAlarmList = useSetRecoilState(deviceSensorAlarmListSelector);

	const PageBody = () => {
		switch (state) {
			case "hasError":
				return (
					<Note
						messageKey={`Error: ${
							deviceSensorAlarmList.message ? deviceSensorAlarmList.message : deviceSensorAlarmList
						}`}
						icon="pi-exclamation-circle fontColour-error"
					/>
				);
			case "loading":
				return (
					<div className="aSelf-center marginBottom-xlarge appMain-content">
						<ProgressSpinner strokeWidth="4" />
					</div>
				);
			case "hasValue": {
				const { grouped, ungrouped } = buildGroupedAlarms(deviceSensorAlarmList);

				return deviceSensorAlarmList.length > 0 ? (
					<div className="appMain-content">
						{grouped.map((group) => (
							<section key={group.id}>
								<header className="flex aItems-center gap-small">
									<h3>
										<Link to={`${nav.alarm.base}/${nav.alarm.group.base}/${group.id}`}>
											{group.name}
										</Link>
									</h3>
									<span className="fontSize-small">
										{t("alarms.groupAlarmCount", { count: group.alarms.length })}
									</span>
								</header>
								<div className="grid columns-3 gap-medium">
									{group.alarms.map((a) => (
										<AlarmCard key={`${group.id}-${a.id}`} alarmData={a} />
									))}
								</div>
							</section>
						))}
						{ungrouped.length > 0 && (
							<section>
								<header className="flex aItems-center gap-small">
									<h3>{t("alarms.ungrouped")}</h3>
									<span className="fontSize-small">
										{t("alarms.groupAlarmCount", { count: ungrouped.length })}
									</span>
								</header>
								<div className="grid columns-3 gap-medium">
									{ungrouped.map((a) => (
										<AlarmCard key={a.id} alarmData={a} />
									))}
								</div>
							</section>
						)}
					</div>
				) : (
					<div className="aSelf-center appMain-content">
						<div className="flexVert aItems-center marginBottom-xlarge">
							<i className="pi pi-check-circle fontColour-success fontSize-xlarge" />
							<p className="fontSize-large marginTop-medium">{t("alarms.none.message")}</p>
						</div>
					</div>
				);
			}
			default:
				return null;
		}
	};

	return (
		<>
			<PageHeader title={t("alarms.pageTitle")} subtitle={t("alarms.pageSubtitle")} />
			<main className="flex appMain">
				<PageBody />
			</main>
		</>
	);
};

export default Alarms;
